import { IntegerField, NumberField } from 'src/shared/entities/control-entities';
import { hasValue } from 'src/shared/utils/common';

import { FormStore } from '../entities/form.entity';
import { GeoLoadRigOperation } from '../entities/geological-load/geo-load-rig-operation.entity';
import { GeoLoadStage } from '../entities/geological-load/geo-load-stage.entity';

import { FormPlugin } from './abstract-form-plugin.entity';

export class GeoLoadDataInterpretationPlugin extends FormPlugin {
  connect(form: FormStore): VoidFunction {
    const calculateDataInterpretation = (): number | null => {
      const count = (() => {
        const pilotsControl = form.fields['pilotNumber'];
        const miniPilotsControl = form.fields['miniPilotNumber'];

        const pilotsCount = (() => {
          if (!(pilotsControl instanceof NumberField) && !(pilotsControl instanceof IntegerField)) {
            return null;
          }

          return pilotsControl.value;
        })();

        const miniPilotsCount = (() => {
          if (!(miniPilotsControl instanceof NumberField) && !(miniPilotsControl instanceof IntegerField)) {
            return null;
          }

          return miniPilotsControl.value;
        })();

        if (!hasValue(pilotsCount) && !hasValue(miniPilotsCount)) {
          return null;
        }

        return (pilotsCount ?? 0) + (miniPilotsCount ?? 0) - 1;
      })();

      if (!hasValue(count) || count < 1) {
        return null;
      }

      return count;
    };

    const mainDataValueChangeDisposer = form.eventBus.subscribe('mainDataValueChange', ({ fieldId, value }) => {
      if (
        !['pilotNumber', 'miniPilotNumber'].includes(fieldId) ||
        form.rigOperationsService.isSectionDetailingOrManyRigOperations
      ) {
        return;
      }

      const {
        geologicalLoadService: { geoLoadRigOperations, geotaskGeoLoad, onValueChange },
      } = form;

      const count: number | null = (() => {
        if (!hasValue(value) || typeof value !== 'number' || value < 2) {
          return null;
        }

        return value - 1;
      })();

      if (geotaskGeoLoad && !geoLoadRigOperations.length) {
        onValueChange('dataInterpretation', !!count, {});
        onValueChange('numberOfDataInterpretationOperations', count, {});
      }

      for (const geoLoadRigOperation of geoLoadRigOperations) {
        if (!geoLoadRigOperation.stages.length) {
          onValueChange('dataInterpretation', !!count, { geoLoadRigOperation });
          onValueChange('numberOfDataInterpretationOperations', count, { geoLoadRigOperation });

          continue;
        }

        for (const geoLoadStage of geoLoadRigOperation.stages) {
          if (geoLoadStage.sections.length) {
            continue;
          }

          onValueChange('dataInterpretation', !!count, { geoLoadRigOperation, geoLoadStage });
          onValueChange('numberOfDataInterpretationOperations', count, { geoLoadRigOperation, geoLoadStage });
        }
      }
    });

    const changeGeoLoadFieldValueDisposer = form.eventBus.subscribe(
      'changeGeoLoadFieldValue',
      ({ fieldId, value, geoLoadRigOperation, geoLoadStage, geoLoadSection }) => {
        if (
          fieldId !== 'dataInterpretation' ||
          !value ||
          geoLoadSection ||
          form.rigOperationsService.isSectionDetailingOrManyRigOperations
        ) {
          return;
        }

        const {
          geologicalLoadService: { onValueChange },
        } = form;

        const count = calculateDataInterpretation();

        if (!hasValue(count) || count < 1) {
          return;
        }

        onValueChange('numberOfDataInterpretationOperations', count, { geoLoadRigOperation, geoLoadStage });
      },
    );

    const deleteRigOperationDisposer = form.eventBus.subscribe('deleteRigOperation', () => {
      if (form.rigOperationsService.isSectionDetailingOrManyRigOperations) {
        return;
      }

      const {
        geologicalLoadService: { geoLoadRigOperations, onValueChange },
      } = form;
      const geoLoadRigOperation: GeoLoadRigOperation | undefined = geoLoadRigOperations[0];
      const geoLoadStage: GeoLoadStage | undefined = geoLoadRigOperation?.stages[0];

      const count = calculateDataInterpretation();

      if (!hasValue(count) || count < 1) {
        return;
      }

      onValueChange('numberOfDataInterpretationOperations', count, { geoLoadRigOperation, geoLoadStage });
    });

    return () => {
      mainDataValueChangeDisposer();
      changeGeoLoadFieldValueDisposer();
      deleteRigOperationDisposer();
    };
  }
}
